import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { createStackScope } from '../index';
import type { SdkConfig } from '../domain/types';
import { StackScope } from '../application/StackScope';

interface StackScopeContextValue {
  instance: StackScope | null;
  isInitialized: boolean;
}

const StackScopeContext = createContext<StackScopeContextValue>({
  instance: null,
  isInitialized: false
});

export interface StackScopeProviderProps {
  children: ReactNode;
  config?: Partial<SdkConfig>;
  autoInit?: boolean;
}

export function StackScopeProvider({ 
  children, 
  config = {}, 
  autoInit = true 
}: StackScopeProviderProps) {
  const [instance, setInstance] = React.useState<StackScope | null>(null);
  const [isInitialized, setIsInitialized] = React.useState(false);

  useEffect(() => {
    if (autoInit && !isInitialized) {
      try {
        const stackscopeInstance = createStackScope(config);
        setInstance(stackscopeInstance);
        setIsInitialized(true);
      } catch (error) {
        console.error('[StackScope] Failed to initialize:', error);
        setIsInitialized(false);
      }
    }
  }, [config, autoInit, isInitialized]);

  const contextValue: StackScopeContextValue = {
    instance,
    isInitialized
  };

  return (
    <StackScopeContext.Provider value={contextValue}>
      {children}
    </StackScopeContext.Provider>
  );
}

export function useStackScopeContext(): StackScopeContextValue {
  const context = useContext(StackScopeContext);
  if (context === undefined) {
    throw new Error('useStackScopeContext must be used within a StackScopeProvider');
  }
  return context;
}